import type { CheckResult } from '../checks.js';
import { fetchUser, noUserHandler, type PageCheckable, type PermissionUser } from '../policyHelper.js';
import { defineSaraaScopePolicy } from './defineSaraaScopePolicy.js';

/** Namespace for every saraa payload type: `@saraa/<scope>`. */
export const SARAA_TYPE_PREFIX = '@saraa';

// The check signature the policy expects. Loosened on the payload so a check
// written against one scope's data shape stays assignable.
type AnyCheck = (user: any, payload: any, perms: any) => Promise<CheckResult>;

/**
 * Thin wrapper that pins saraa's conventions: the `@saraa` type prefix, the
 * isomorphic `fetchUser`, and deny-on-anonymous.
 *
 * There is no longer an `isAdmin` check layered onto every action. A platform
 * admin's grants are global (`checks: []`), so the policy short-circuits to
 * allowed before any check runs. The old wrapper existed only because grants from
 * several roles merged by concatenating their check arrays, which turned a global
 * admin grant into a check-scoped one; a user resolves to exactly one grant set
 * now, so there is nothing to merge.
 */
export const defineSaraaScope =
  <D extends PageCheckable = PageCheckable>() =>
  <S extends string, A extends readonly string[]>(config: {
    scope: S;
    actions: A;
    checks?: Partial<Record<A[number], Record<string, AnyCheck>>>;
  }) =>
    defineSaraaScopePolicy<D, PermissionUser>()({
      scope: config.scope,
      typePrefix: SARAA_TYPE_PREFIX,
      actions: config.actions,
      fetchUser,
      noUserHandler,
      checks: config.checks as never,
    });
